import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import NotFound from "pages/NotFound";
import PDFPreview from './pages/pdf-preview';
import SubmissionConfirmation from './pages/submission-confirmation';
import ReferralForm from './pages/referral-form';
import PublicPractitionerReferral from './pages/public-practitioner-referral';
import EndoReferralLandingPage from './pages/endo-referral-landing-page';
import ReferralManagementDashboard from './pages/referral-management-dashboard';
import Login from './pages/login';
import SuperAdminDashboard from './pages/super-admin-dashboard';
import PractitionerRegistration from './pages/practitioner-registration';
import PractitionerDashboard from './pages/practitioner-dashboard';
import PractitionerAccessLinkGenerator from './pages/practitioner-access-link-generator';
import PractitionerManagementDashboard from './pages/practitioner-management-dashboard';
import IndividualPractitionerDashboard from './pages/individual-practitioner-dashboard';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider } from './contexts/AuthContext';
import EmailManagementDashboard from './pages/email-management-dashboard';
import AdvancedAnalyticsDashboard from './pages/advanced-analytics-dashboard';
import PractitionerReportsDashboard from './pages/practitioner-reports-dashboard';
import PasswordReset from './pages/password-reset';
import AdvancedSystemAnalyticsAuditDashboard from './pages/advanced-system-analytics-audit-dashboard';
import SuperAdminUserManagement from './pages/super-admin-user-management';
import CredentialManagementEmailSystem from './pages/credential-management-email-system';
import AIReportGenerator from './pages/ai-report-generator';
import ReportEditorPreview from './pages/report-editor-preview';
import ReportManagementDashboard from './pages/report-management-dashboard';

const Routes = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ErrorBoundary>
          <ScrollToTop />
          <RouterRoutes>
            {/* Public Routes */}
            <Route path="/" element={<EndoReferralLandingPage />} />
            <Route path="/referral-form" element={<ReferralForm />} />
            <Route path="/p/:slug" element={<PublicPractitionerReferral />} />
            <Route path="/p/:slug/pdf-preview" element={<PDFPreview />} />
            <Route path="/p/:slug/confirmation" element={<SubmissionConfirmation />} />
            <Route path="/pdf-preview" element={<PDFPreview />} />
            <Route path="/submission-confirmation" element={<SubmissionConfirmation />} />
            
            {/* Authentication - Unified login page with role-based redirect */}
            <Route path="/login" element={<Login />} />
            <Route path="/password-reset" element={<PasswordReset />} />
            <Route path="/reset-password" element={<PasswordReset />} /> {/* Alias for backward compatibility */}
            
            {/* Protected Routes - Super Admin */}
            <Route 
              path="/super-admin-dashboard" 
              element={
                <ProtectedRoute requireAdmin={true}>
                  <SuperAdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/advanced-analytics-dashboard" 
              element={
                <ProtectedRoute requireAdmin={true}>
                  <AdvancedAnalyticsDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/advanced-system-analytics-audit-dashboard" 
              element={
                <ProtectedRoute requireAdmin={true}>
                  <AdvancedSystemAnalyticsAuditDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/email-management-dashboard" 
              element={
                <ProtectedRoute requireAdmin={true}>
                  <EmailManagementDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/practitioner-registration" 
              element={
                <ProtectedRoute requireAdmin={true}>
                  <PractitionerRegistration />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/practitioner-access-link-generator" 
              element={
                <ProtectedRoute requireAdmin={true}>
                  <PractitionerAccessLinkGenerator />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/referral-management-dashboard" 
              element={
                <ProtectedRoute requireAdmin={true}>
                  <ReferralManagementDashboard />
                </ProtectedRoute>
              } 
            />
            
            {/* Protected Routes - Practitioner */}
            <Route 
              path="/practitioner-dashboard" 
              element={
                <ProtectedRoute requireAdmin={true}>
                  <PractitionerDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/individual-practitioner-dashboard" 
              element={
                <ProtectedRoute requireAdmin={false}>
                  <IndividualPractitionerDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/practitioner-management-dashboard" 
              element={
                <ProtectedRoute requireAdmin={false}>
                  <PractitionerManagementDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/practitioner-reports-dashboard" 
              element={
                <ProtectedRoute requireAdmin={false}>
                  <PractitionerReportsDashboard />
                </ProtectedRoute>
              } 
            />
            
            {/* AI Report Generator Routes */}
            <Route 
              path="/ai-report-generator" 
              element={
                <ProtectedRoute requireAdmin={false}>
                  <AIReportGenerator />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/report-editor-preview" 
              element={
                <ProtectedRoute requireAdmin={false}>
                  <ReportEditorPreview />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/report-management-dashboard" 
              element={
                <ProtectedRoute requireAdmin={false}>
                  <ReportManagementDashboard />
                </ProtectedRoute>
              } 
            />
            
            {/* Super Admin User Management */}
            <Route 
              path="/super-admin-user-management" 
              element={
                <ProtectedRoute requireAdmin={true}>
                  <SuperAdminUserManagement />
                </ProtectedRoute>
              } 
            />
            
            {/* Credential Management & Email System */}
            <Route 
              path="/credential-management-email-system" 
              element={
                <ProtectedRoute requireAdmin={true}>
                  <CredentialManagementEmailSystem />
                </ProtectedRoute>
              } 
            />
            
            {/* 404 */}
            <Route path="*" element={<NotFound />} />
          </RouterRoutes>
        </ErrorBoundary>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default Routes;
